// Importing necessary UI components and utilities
import { DialogCloseTrigger, DialogRoot } from '@chakra-ui/react';
import styles from './styles.module.scss'; // Local scoped styles
import Dialog from 'components/dialog'; // Reusable Dialog wrapper
import Title from 'components/typografy/title'; // Custom title typography
import Text from 'components/typografy/text'; // Custom text typography
import classNames from 'classnames'; // Utility to combine CSS classes
import React, { useCallback } from 'react';
import useIsMobile from '../../hooks/useIsMobile';

// Importing app-specific context and constants
import { useModalContext } from 'providers/modal-context'; // Context that controls modal state and content
import { ModalActions } from 'enums/api'; // Enum of possible modal actions
import { sendVerifyEmailLink } from 'api/auth'; // API call to trigger sending verification email
import StyledButton from '../styled-button'; // Custom styled button

/*
 * Modal that appears after server responses or based on specific modal context state.
 * Uses `modalContext` to decide what action to take when the user clicks the button.
 */

const ApiModal = () => {
  // Destructure modal state and content from context
  const {
    isOpen, // Whether the modal is currently visible
    setIsOpen, // Function to toggle modal visibility
    modalContext: {
      title, // Title text shown in the modal
      displayMessage, // Body text shown in the modal
      btnAction, // What action to perform when user clicks the button
      buttonText, // Label for the button
      type // Style variant for the modal and button (e.g., 'success', 'error')
    }
  } = useModalContext();

  // Determine if the device is mobile to apply responsive styles
  const isMobile = useIsMobile();

  /*
   * Defines the function to run when the modal button is clicked.
   * This changes based on what `btnAction` is set to.
   */
  const actionFn = useCallback(async () => {
    try {
      switch (btnAction) {
        case ModalActions.SEND_VERIFY_MAIL:
          // Call API to send a new verification email
          return await sendVerifyEmailLink();
        case ModalActions.CLOSE_MODAL:
          // Do nothing, just close the modal
          return null;
        default:
          // For unknown actions, just close the modal
          return;
      }
    } catch (error) {
      // Handle any errors that occur during the API call
      return; // prevent unhandled promise rejection
    }
  }, [btnAction]);

  // If the modal is not open, don't render anything
  if (!isOpen) {
    return null;
  }


  // Renders the modal dialog UI
  return (
    <DialogRoot
      scrollBehavior="inside" // Enables scroll within the dialog if content overflows
      size={isMobile? "cover" : "sm"}   // "sm" for desktop, "cover" for mobile
      onOpenChange={() => setIsOpen(!isOpen)} // Toggles modal open state
      onCloseChange={() => setIsOpen(!isOpen)} // Also toggles on close
      defaultOpen={true} // Open on initial render
    >
      <Dialog isOpen={true} contentClassNames={styles.dialog}>
        {/* Modal Title with styling based on type (e.g., success/error) */}
        <Title variant={'h1'} nativeClassNames={classNames(styles.title, styles[type])}>
          {title}
        </Title>

        {/* Modal Message */}
        <Text variant={'medium'}>{displayMessage}</Text>

        {/* Button Row */}
        <div className={styles.row}>
          {/* When this button is clicked, it will call `actionFn` and also close the modal */}
          <DialogCloseTrigger>
            <StyledButton isLoading={false} variant={type} onClick={actionFn}>
              {buttonText && buttonText}
            </StyledButton>
          </DialogCloseTrigger>
        </div>
      </Dialog>
    </DialogRoot>
  );
};

export default ApiModal;